import { useCallback, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import './HorizontalSplitPane.css';

const HANDLE_WIDTH_PX = 8;

type Props = {
  left: ReactNode;
  right: ReactNode;
  /** Left pane width as a fraction of the content area (excluding handle), 0–1. */
  defaultRatio?: number;
  minLeftPx?: number;
  minRightPx?: number;
  storageKey?: string;
  className?: string;
};

function loadRatio(key: string | undefined, fallback: number): number {
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const value = Number(raw);
    if (Number.isFinite(value) && value >= 0.15 && value <= 0.85) return value;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function HorizontalSplitPane({
  left,
  right,
  defaultRatio = 2 / 3,
  minLeftPx = 320,
  minRightPx = 240,
  storageKey,
  className,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState(() => loadRatio(storageKey, defaultRatio));
  const ratioRef = useRef(ratio);
  ratioRef.current = ratio;
  const draggingRef = useRef(false);

  const updateRatioFromPointer = useCallback(
    (clientX: number) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const contentWidth = rect.width - HANDLE_WIDTH_PX;
      if (contentWidth <= minLeftPx + minRightPx) return;

      const rawLeft = clientX - rect.left;
      const leftWidth = Math.min(Math.max(rawLeft, minLeftPx), contentWidth - minRightPx);
      setRatio(leftWidth / contentWidth);
    },
    [minLeftPx, minRightPx]
  );

  const onHandlePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.classList.add('horizontal-split-dragging');
    updateRatioFromPointer(event.clientX);
  }, [updateRatioFromPointer]);

  const onHandlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      updateRatioFromPointer(event.clientX);
    },
    [updateRatioFromPointer]
  );

  const endDrag = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.classList.remove('horizontal-split-dragging');
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, String(ratioRef.current));
      } catch {
        /* ignore */
      }
    }
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const leftPct = Math.round(ratio * 100);

  return (
    <div
      ref={containerRef}
      className={['horizontal-split-pane', className].filter(Boolean).join(' ')}
      style={{ ['--split-left-ratio' as string]: String(ratio) }}
    >
      <div className="horizontal-split-pane-left">{left}</div>
      <div
        className="horizontal-split-pane-handle"
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={leftPct}
        aria-valuemin={15}
        aria-valuemax={85}
        aria-label="调整左右区域宽度"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
      <div className="horizontal-split-pane-right">{right}</div>
    </div>
  );
}
